import type { ReactNode } from 'react';
import { createRoute, redirect, useNavigate, type AnyRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { listSearchSchema } from '@eis/core';
import { billingPermissions as p } from '../permissions';
import { InvoiceListPage } from '../features/invoices/ui/InvoiceListPage';
import { InvoiceDetailPage } from '../features/invoices/ui/InvoiceDetailPage';
import { BillRunsPage } from '../features/runs/ui/BillRunsPage';

/**
 * The billing route tree (backbone §3): `/billing/invoices`, `/billing/invoices/$code`,
 * `/billing/runs` (SDD-015 §7.1; modules/billing/PERMISSIONS.md "Route groups").
 *
 * Permissions are declared ONCE here in `staticData`; `beforeLoad` enforces them and the
 * manifest's nav is filtered from the same declarations, so guard and menu cannot drift apart
 * (Addendum A.2). The search params are Zod-validated: `policy` and `action` are what the
 * insurance pages hand over through `links.billing.invoices(...)` to open one of this module's
 * dialogs about one policy (backbone §1 — a link, never an import); `month` is `YYYY-MM`.
 *
 * The shell composes module route trees at runtime rather than through TanStack's file-based
 * codegen, so the parent is `AnyRoute` and param/search inference cannot reach these components;
 * the schemas below are the runtime guarantee, and the narrowing goes through `unknown`.
 */
const invoicesSearchSchema = listSearchSchema.extend({
  policy: z.string().optional(),
  action: z.enum(['issue', 'period', 'correction', 'schedule', 'manual']).optional(),
  month: z.string().regex(/^\d{4}-\d{2}$/).optional(),
});
type InvoicesSearch = z.infer<typeof invoicesSearchSchema>;
interface InvoiceParams {
  readonly code: string;
}

interface GuardContext {
  readonly permissions: { canGroups: (groups: readonly (readonly string[])[]) => boolean };
}

const guard =
  (groups: readonly (readonly string[])[]) =>
  ({ context }: { context: GuardContext }): void => {
    if (!context.permissions.canGroups(groups)) {
      // TanStack signals navigation by throwing this sentinel — control flow, not an error.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/403' });
    }
  };

export function createRoutes(parentRoute: AnyRoute): AnyRoute[] {
  const invoicesIndex = createRoute({
    getParentRoute: () => parentRoute,
    path: '/invoices',
    validateSearch: invoicesSearchSchema,
    staticData: { permissions: [[p.invoices.view]] },
    beforeLoad: guard([[p.invoices.view]]),
    component: function InvoicesRoute(): ReactNode {
      const navigate = useNavigate();
      const { policy, action } = invoicesIndex.useSearch() as unknown as InvoicesSearch;
      return (
        <InvoiceListPage
          // Keyed on the action so a link that opens a dialog mounts it fresh, prefilled.
          key={`${policy ?? ''}:${action ?? ''}`}
          policyCode={policy ?? ''}
          action={action ?? null}
          onDialogClosed={() => {
            if (action === undefined) return;
            // The dialog is closed; the URL stops asking for it, and a reload does not reopen
            // it. The list's own state (filters, search, order, month) stays.
            void navigate({
              to: '/billing/invoices',
              search: (previous: Record<string, unknown>) =>
                Object.fromEntries(
                  Object.entries(previous).filter(([key]) => key !== 'policy' && key !== 'action'),
                ),
            });
          }}
        />
      );
    },
  });

  const invoiceDetail = createRoute({
    getParentRoute: () => parentRoute,
    path: '/invoices/$code',
    staticData: { permissions: [[p.invoices.view]] },
    beforeLoad: guard([[p.invoices.view]]),
    component: function InvoiceDetailRoute(): ReactNode {
      const { code } = invoiceDetail.useParams() as unknown as InvoiceParams;
      return <InvoiceDetailPage code={code} />;
    },
  });

  const runsIndex = createRoute({
    getParentRoute: () => parentRoute,
    path: '/runs',
    validateSearch: listSearchSchema,
    staticData: { permissions: [[p.billRuns.view]] },
    beforeLoad: guard([[p.billRuns.view]]),
    component: BillRunsPage,
  });

  return [invoicesIndex, invoiceDetail, runsIndex];
}
